/**
 * Comprehensive DISC Personality Content for Frontend
 * Professional descriptions for assessment reports
 */

export const personalityContent = {
  D: {
    name: 'Dominant',
    letter: 'D',
    subtitle: 'Outgoing & Task-Oriented',
    color: '#DC2626',
    characteristics: ['Direct', 'Decisive', 'Doer'],

    descriptiveWords: [
      'Determined', 'Competitive', 'Bold',
      'Decisive', 'Direct', 'Strong-willed',
      'Self-confident', 'Results-oriented', 'Forceful',
      'Risk-taker', 'Problem solver', 'Independent',
      'Adventurous', 'Demanding', 'Inquisitive',
      'Daring', 'Pioneering', 'Driving',
      'Productive', 'Persistent'
    ],

    strengthsParagraphs: [
      'You have a natural ability to take charge and lead others. Your confidence and decisiveness inspire those around you to take action and achieve results.',
      'You thrive in challenging situations where quick decisions need to be made. Your ability to see the big picture while maintaining focus on goals makes you an effective problem solver.',
      'Your determination and persistence help you overcome obstacles that might discourage others. You have a strong drive to succeed and are not easily deterred by setbacks.',
      'You are naturally results-oriented and focused on achieving objectives. Your competitive nature pushes you to excel and continuously improve.',
      'Your direct communication style ensures clarity and minimizes misunderstandings. You value efficiency and getting things done right the first time.'
    ],

    keysToExcellence: [
      'Explain the reasoning behind your decisions to help others understand your perspective and feel included in the process.',
      'Take time to acknowledge and celebrate the contributions of others. Recognition motivates team members and builds loyalty.',
      'Practice active listening by giving others your full attention before responding. Consider perspectives different from your own.',
      'Show empathy by considering how your actions and decisions affect others emotionally. Small gestures of care strengthen relationships.',
      'Be patient with processes that require time. Not everything can or should be rushed to completion.',
      'Delegate tasks and trust others to complete them. Avoid the temptation to take over when things don\'t move as fast as you\'d like.'
    ],

    teamMember: {
      mainStrength: 'Bottom-line Organizer',
      mainMotivation: 'Directing and control; new opportunities and challenges',
      talents: 'Getting results quickly; making quick decisions; persistence; solving problems',
      valueToTeam: 'Pushes team toward goals and results; takes charge in difficult situations; challenges status quo',
      idealEnvironment: 'Freedom from supervision; new challenges and ideas to work on; nonroutine work with opportunity for advancement',
      keysToMotivate: 'Authority, challenges, prestige, freedom, varied activities, growth opportunities, independence',
      possibleBlindSpots: 'May be insensitive to others\' feelings; may overlook details; may try to do too much personally; impatience with routines'
    },

    overview: 'You are a person who tends to be direct, decisive, and determined. You naturally take charge of situations and are motivated by challenges and opportunities to achieve results. Your strong will and competitive nature drive you to succeed.'
  },

  I: {
    name: 'Inspiring',
    letter: 'I',
    subtitle: 'Outgoing & People-Oriented',
    color: '#F58820',
    characteristics: ['Influencing', 'Interactive', 'Involved'],

    descriptiveWords: [
      'Enthusiastic', 'Optimistic', 'Persuasive',
      'Talkative', 'Impulsive', 'Emotional',
      'Inspiring', 'Magnetic', 'Political',
      'Demonstrative', 'Animated', 'Stimulating',
      'Generous', 'Poised', 'Charming',
      'Confident', 'Convincing', 'Warm',
      'Dramatic', 'Expressive'
    ],

    strengthsParagraphs: [
      'You have a natural gift for connecting with people and building relationships. Your enthusiasm and optimism are contagious and inspire others.',
      'Your excellent communication skills enable you to persuade and influence others effectively. You can articulate ideas in compelling ways that motivate action.',
      'You bring energy and excitement to any team or project. Your positive attitude helps maintain morale even during challenging times.',
      'Your creativity and ability to think outside the box lead to innovative solutions. You naturally see possibilities and opportunities others might miss.',
      'You excel at networking and creating collaborative environments. Your people skills make others feel valued and appreciated.'
    ],

    keysToExcellence: [
      'Focus on following through on commitments and completing tasks before moving on to new exciting projects.',
      'Pay attention to important details that may affect outcomes. Take time to review work for accuracy.',
      'Be more objective in your decision-making. Balance your enthusiasm with realistic assessment of situations.',
      'Manage your time effectively by prioritizing tasks and avoiding overcommitment.',
      'Listen carefully to others before responding. Sometimes the best influence comes from understanding first.',
      'Document important discussions and agreements to ensure clarity and accountability.'
    ],

    teamMember: {
      mainStrength: 'Inspires others to action',
      mainMotivation: 'Recognition, social acceptance, and group activities',
      talents: 'Enthusiasm, persuading and motivating others, entertaining, networking, creative problem-solving',
      valueToTeam: 'Optimism and enthusiasm; creative ideas; motivates and inspires others; peacemaker and team builder',
      idealEnvironment: 'Freedom of expression; democratic relationships; freedom from control and detail; opportunity to verbalize',
      keysToMotivate: 'Public recognition, group activities, freedom from detail, being part of the team, coaching opportunities',
      possibleBlindSpots: 'May talk too much; may not follow through; may trust too easily; avoids confrontation; disorganized'
    },

    overview: 'You are a person who tends to be enthusiastic, optimistic, and influential. You naturally connect with people and are motivated by recognition and social interactions. Your positive energy inspires others to take action.'
  },

  S: {
    name: 'Supportive',
    letter: 'S',
    subtitle: 'Reserved & People-Oriented',
    color: '#2563EB',
    characteristics: ['Steady', 'Stable', 'Sweet (kind)'],

    descriptiveWords: [
      'Patient', 'Predictable', 'Reliable',
      'Steady', 'Relaxed', 'Modest',
      'Stable', 'Neighborly', 'Tactful',
      'Passive', 'Consistent', 'Team player',
      'Possessive', 'Complacent', 'Friendly',
      'Kind', 'Understanding', 'Amiable',
      'Tolerant', 'Deliberate'
    ],

    strengthsParagraphs: [
      'You are a loyal and dependable team member who provides stability and consistency. Others can always count on you to follow through on commitments.',
      'Your patient and understanding nature makes you an excellent listener and counselor. You create safe spaces where others feel comfortable sharing.',
      'You excel at maintaining harmony and building consensus within teams. Your diplomatic approach helps resolve conflicts peacefully.',
      'Your steady and methodical approach ensures quality and thoroughness in everything you do. You don\'t rush through tasks at the expense of quality.',
      'You are naturally supportive of others and derive satisfaction from helping them succeed. Your selfless nature builds deep, lasting relationships.'
    ],

    keysToExcellence: [
      'Be more open to change and new approaches. Sometimes different methods can lead to better outcomes.',
      'Express your opinions and needs more clearly. Your insights are valuable and deserve to be heard.',
      'Learn to say no when necessary. Setting healthy boundaries protects your well-being and energy.',
      'Take initiative on new projects rather than waiting for direction. Your ideas have merit.',
      'Address conflicts directly rather than avoiding them. Early resolution prevents larger problems.',
      'Embrace opportunities for growth even if they feel uncomfortable at first.'
    ],

    teamMember: {
      mainStrength: 'Reliable team player',
      mainMotivation: 'Security, stability, and appreciation from others',
      talents: 'Listening, patience, developing specialized skills, reliability, finishing tasks, following through',
      valueToTeam: 'Dependable and consistent; brings harmony to the team; loyal to team members; patient with others',
      idealEnvironment: 'Stable environment; sincere appreciation; minimal conflict; time to adjust to change; clear expectations',
      keysToMotivate: 'Security, genuine appreciation, traditional procedures, understanding, low-risk assignments',
      possibleBlindSpots: 'May resist change; difficulty prioritizing; may take on too much; may stuff feelings; indecisive'
    },

    overview: 'You are a person who tends to be patient, reliable, and supportive. You value stability and consistency, and are motivated by helping others and maintaining harmony. Your steady nature provides a calming influence on those around you.'
  },

  C: {
    name: 'Cautious',
    letter: 'C',
    subtitle: 'Reserved & Task-Oriented',
    color: '#EAB308',
    characteristics: ['Calculating', 'Competent', 'Careful'],

    descriptiveWords: [
      'Accurate', 'Analytical', 'Conscientious',
      'Careful', 'Fact-finder', 'Precise',
      'High standards', 'Systematic', 'Diplomatic',
      'Courteous', 'Reserved', 'Restrained',
      'Logical', 'Perfectionist', 'Orderly',
      'Detailed', 'Inquisitive', 'Sensitive',
      'Thorough', 'Aesthetic'
    ],

    strengthsParagraphs: [
      'You have exceptional attention to detail and commitment to accuracy. Your thorough approach ensures high-quality outcomes.',
      'Your analytical thinking enables you to solve complex problems methodically. You consider all factors before making decisions.',
      'You maintain high standards and take pride in doing things correctly. Your conscientiousness is a valuable asset to any team.',
      'Your systematic approach to work ensures consistency and reliability. You follow through on commitments with precision.',
      'You excel at research and gathering information. Your fact-based approach leads to well-informed decisions and recommendations.'
    ],

    keysToExcellence: [
      'Accept that perfection is not always attainable or necessary. Learn to recognize when "good enough" is appropriate.',
      'Share your knowledge and expertise more freely with others. Your insights can help the entire team improve.',
      'Take action even when you don\'t have all the information. Sometimes waiting for complete data causes missed opportunities.',
      'Focus on the big picture sometimes rather than getting lost in details. Strategic thinking complements your analytical strengths.',
      'Be more open to input from others, even if their approach differs from yours.',
      'Take calculated risks occasionally. Not every decision requires exhaustive analysis.'
    ],

    teamMember: {
      mainStrength: 'Ensures quality and accuracy',
      mainMotivation: 'Quality work and being right; following established procedures',
      talents: 'Planning, organization, analysis, following directives and standards, checking for accuracy',
      valueToTeam: 'Maintains high standards; brings objectivity; defines situations; gathers data; ensures accuracy',
      idealEnvironment: 'Clear expectations; quality focus; reserved atmosphere; opportunity to demonstrate expertise; limited risk',
      keysToMotivate: 'Clearly defined expectations, quality work, logical approach, time to analyze, value of expertise',
      possibleBlindSpots: 'May get bogged down in details; may be too critical; fear of criticism; avoiding risk; analysis paralysis'
    },

    overview: 'You are a person who tends to be analytical, accurate, and conscientious. You value quality and precision, and are motivated by doing things correctly. Your attention to detail ensures excellence in your work.'
  },

  LEVEL: {
    name: 'Level (Balanced)',
    letter: 'L',
    subtitle: 'Balanced Blend of All Styles',
    color: '#6B7280',
    characteristics: ['Adaptable', 'Versatile', 'Balanced'],

    descriptiveWords: [
      'Adaptable', 'Versatile', 'Flexible',
      'Balanced', 'Well-rounded', 'Diplomatic',
      'Moderate', 'Even-tempered', 'Considerate',
      'Practical', 'Reasonable', 'Cooperative',
      'Thoughtful', 'Measured', 'Composed',
      'Fair-minded', 'Accommodating', 'Level-headed',
      'Multifaceted', 'Dynamic'
    ],

    strengthsParagraphs: [
      'You have a unique ability to adapt your style to different situations and people. This flexibility makes you effective in a wide range of contexts.',
      'Your balanced approach allows you to see situations from multiple perspectives. You can appreciate the viewpoints of different personality types.',
      'You can shift between being task-focused and people-focused as the situation requires. This versatility is a valuable leadership quality.',
      'Your even-tempered nature helps you remain calm and composed under pressure. You provide stability while remaining open to change.',
      'You can relate well to all types of people because you share characteristics with each personality style. This makes you an excellent mediator and team member.'
    ],

    keysToExcellence: [
      'Develop deeper expertise in areas where you\'d like to specialize. Your adaptability is a strength, but depth of knowledge in key areas adds power.',
      'Take clear stands on important issues rather than always seeking middle ground. Sometimes decisive action is needed.',
      'Recognize which situations call for which style and intentionally adapt your approach for maximum effectiveness.',
      'Build on your natural ability to connect with different personality types by studying their preferences and communication styles.',
      'Use your balanced perspective to help teams understand and appreciate their differences.',
      'Don\'t be afraid to lean into one of your style tendencies when the situation clearly calls for it.'
    ],

    teamMember: {
      mainStrength: 'Adaptability and versatility',
      mainMotivation: 'Balance, harmony, and being valued for contributions',
      talents: 'Adapting to situations, seeing multiple perspectives, mediating between different viewpoints, flexibility',
      valueToTeam: 'Can work well with all personality types; brings balance and perspective; fills gaps where needed; diplomatic',
      idealEnvironment: 'Varied responsibilities; opportunity to use different skills; collaborative atmosphere; recognition for versatility',
      keysToMotivate: 'Recognition of adaptability, varied challenges, opportunity to work with different people, balanced workload',
      possibleBlindSpots: 'May lack strong direction; difficulty making decisive choices; may spread too thin; identity uncertainty in some contexts'
    },

    overview: 'You have a balanced blend of all four DISC personality styles. This unique profile gives you the ability to adapt to different situations and relate well to all types of people. Your versatility is a significant strength in today\'s dynamic work environment.'
  }
};

export const discModelExplanation = {
  title: 'The "DISC" Model of Human Behavior',
  intro: 'The DISC model describes four primary behavioral styles that help us understand how people approach tasks and relationships.',
  styles: {
    D: {
      name: 'Dominant',
      description: 'Outgoing & Task-Oriented. Dominant personalities are direct, decisive, and driven by results. They take charge and focus on the bottom line.',
    },
    I: {
      name: 'Inspiring',
      description: 'Outgoing & People-Oriented. Inspiring personalities are enthusiastic, optimistic, and focused on relationships. They motivate and influence others.',
    },
    S: {
      name: 'Supportive',
      description: 'Reserved & People-Oriented. Supportive personalities are patient, reliable, and focused on stability. They value harmony and helping others.',
    },
    C: {
      name: 'Cautious',
      description: 'Reserved & Task-Oriented. Cautious personalities are analytical, accurate, and focused on quality. They value precision and doing things right.',
    },
  },
  understanding: 'Understanding your DISC style helps you recognize your natural tendencies, leverage your strengths, and adapt your approach when working with others who have different styles.',
};

export const graphScaleLabels = [
  { value: 100, label: 'Very High' },
  { value: 80, label: 'High' },
  { value: 60, label: 'Avg Above' },
  { value: 50, label: 'Midline' },
  { value: 40, label: 'Avg Below' },
  { value: 20, label: 'Low' },
  { value: 0, label: 'Very Low' },
];

// Helper to get content for a style (including LEVEL detection)
export const getContentForStyle = (primaryStyle, blend) => {
  if (blend === 'LEVEL') {
    return personalityContent.LEVEL;
  }
  return personalityContent[primaryStyle] || personalityContent.D;
};
